import {
  LAYER_KEYS,
  isLayerKey,
  seatSeedDeltasById,
  seatSeedDeltasForRole,
  MUNICIPAL_EOC_SEED_ID,
  type ImsRole,
  type SavedProfileView,
  type SeatProfileSeed,
} from '@tsr/shared-types';
import {
  defaultLayerPrefs,
  prefsToLayerMap,
  applyServerLayerMap,
  LAYER_KEY_TO_PREF,
  type LayerPrefs,
  type ServerLayerPrefs,
} from './prefs';
import type { PinnedFeed } from './pinnedFeeds';

/**
 * Seat role-profiles (#187) — the pure apply/snapshot engine.
 *
 * A profile's `layerDeltas` are DELTAS over the locked default view (#202), so
 * applying one is deterministic: locked defaults + deltas, regardless of what
 * the operator toggled beforehand. Switching seats therefore never compounds —
 * Situation Unit → Logistics lands on exactly the Logistics shape, and applying
 * an empty delta set IS the default 4-layer COP ("Default view" in the picker).
 *
 * Out of the layer reset, deliberately:
 *   - `briefing-mode` — a view MODE, not a seat layer; switching seats must not
 *     yank a minister-briefing screen back to Operator (it is also excluded from
 *     snapshots, below).
 *   - the non-LAYER_KEYS personal fields (hidden partner/aircraft-role
 *     sub-toggles, expanded categories) — per-operator fine-tuning that follows
 *     the person, not the seat.
 *
 * Snapshots are the inverse: diff the operator's current layer map against the
 * locked defaults and keep only the differing keys — so "Save current as
 * profile" stores the smallest honest delta, and a stored profile stays valid
 * if the founder later edits DEFAULT_VIEW_LAYERS (the unchanged layers follow
 * the new baseline instead of being fossilized).
 */

/** Layer keys excluded from both apply and snapshot (view mode, not seat shape). */
const PROFILE_EXEMPT_KEYS = new Set<string>(['briefing-mode']);

/** Apply a profile's layer deltas: locked defaults + deltas, preserving the
 *  operator's personal (non-seat) fields from their current prefs. Unknown delta
 *  keys are ignored (forward-compatible, same stance as the prefs PUT). */
export function applyProfileLayerDeltas(
  current: LayerPrefs,
  layerDeltas: Record<string, boolean>,
): LayerPrefs {
  const next = defaultLayerPrefs();
  // Personal fine-tuning + view mode follow the operator, not the seat.
  next.hiddenPartners = current.hiddenPartners;
  next.expandedCategories = current.expandedCategories;
  next.hiddenAircraftRoles = current.hiddenAircraftRoles;
  next.briefingMode = current.briefingMode;
  for (const [key, value] of Object.entries(layerDeltas)) {
    if (!isLayerKey(key) || PROFILE_EXEMPT_KEYS.has(key) || typeof value !== 'boolean') continue;
    (next[LAYER_KEY_TO_PREF[key]] as boolean) = value;
  }
  return next;
}

/** Snapshot the current prefs as the smallest delta set over the locked
 *  defaults — the payload of "Save current as profile". */
export function snapshotProfileLayerDeltas(current: LayerPrefs): Record<string, boolean> {
  const currentMap = prefsToLayerMap(current);
  const defaultMap = prefsToLayerMap(defaultLayerPrefs());
  const deltas: Record<string, boolean> = {};
  for (const key of LAYER_KEYS) {
    if (PROFILE_EXEMPT_KEYS.has(key)) continue;
    if (currentMap[key] !== defaultMap[key]) deltas[key] = currentMap[key]!;
  }
  return deltas;
}

/** Build the full profile view for "Save current as profile": layer deltas +
 *  the panel arrangement (the operator's pinned feeds, snapshot read-only from
 *  the pin store). */
export function snapshotProfileView(current: LayerPrefs, pins: readonly PinnedFeed[]): SavedProfileView {
  const view: SavedProfileView = { layerDeltas: snapshotProfileLayerDeltas(current) };
  if (pins.length > 0) {
    view.panelArrangement = {
      pinnedFeeds: pins.map((p) => ({ kind: p.kind, id: p.id, label: p.label })),
    };
  }
  return view;
}

/** The picker's "Default view" pseudo-profile — applying it is the locked
 *  default 4-layer COP (empty deltas). Not a seed (not a doctrinal seat). */
export const DEFAULT_VIEW_PROFILE: Pick<SeatProfileSeed, 'id' | 'label' | 'description'> = {
  id: 'seed:default-view',
  label: 'Default view',
  description: 'The locked 4-layer baseline COP',
};

/** What the Settings picker hands App when the operator applies a profile —
 *  uniform across the curated seeds, the Default-view pseudo-profile, and
 *  custom saved rows. */
export interface SeatProfileTarget {
  id: string;
  label: string;
  view: SavedProfileView;
  /** true for the code-constant seeds / Default view (no DB row behind them). */
  isSeed: boolean;
}

/**
 * The race-free FIRST-RUN boot decision (#458, role-view council §7 step 4).
 *
 * Resolves what the COP boots into in ONE pure step, so App applies it in ONE
 * setPrefs (no mount-applied seed for a later async boot to clobber — front-end
 * architect's blocker). Precedence:
 *   1. server === null (offline / 5xx)     → keep the localStorage-backed prefs
 *      untouched (the existing L.1.5 #1/#2 offline-safe behavior).
 *   2. server.hasStored (returning op)      → server wins; their stored toggles
 *      survive and the boot NEVER re-flattens their customizations.
 *   3. else (FIRST RUN, no stored rows)     → seed from the role's seat profile;
 *      an unmapped role (admin / eoc-director / viewer) or no role seeds an EMPTY
 *      delta set, which is DEFAULT_VIEW_LAYERS byte-identical (the lock untouched).
 *
 * The seed is applied as DELTAS over the locked default (applyProfileLayerDeltas),
 * never an absolute set — so it can never fork or fossilize DEFAULT_VIEW_LAYERS.
 */
export function resolveBootLayerPrefs(
  current: LayerPrefs,
  server: ServerLayerPrefs | null,
  role: ImsRole | null,
): LayerPrefs {
  if (!server) return current;
  if (server.hasStored) return applyServerLayerMap(current, server.preferences);
  const deltas = (role ? seatSeedDeltasForRole(role) : null) ?? {};
  return applyProfileLayerDeltas(current, deltas as Record<string, boolean>);
}

/**
 * Enter the PEOC "view as <community>" TRANSIENT lens (#458, council §3).
 *
 * Snapshots the operator's ACTUAL current prefs (so exit can restore exactly THEM
 * — not the bare default, which would drop their own customizations: the PEOC +
 * architect convergent blocker) and produces the Municipal-EOC layer view to glance
 * through. Pure — App owns the refs, the map extent, and the non-persistence
 * (it must NOT queueServerLayerSync and must suppress the localStorage write while
 * the lens is up, else the glance stealth-persists as the boot default).
 */
export function enterMunicipalLens(current: LayerPrefs): {
  snapshot: LayerPrefs;
  lens: LayerPrefs;
} {
  return {
    snapshot: current,
    lens: applyProfileLayerDeltas(
      current,
      (seatSeedDeltasById(MUNICIPAL_EOC_SEED_ID) ?? {}) as Record<string, boolean>,
    ),
  };
}

/**
 * Exit the lens: restore EXACTLY the snapshot taken on enter. NEVER re-derive from
 * defaultLayerPrefs() — applyProfileLayerDeltas is not its own inverse (it rebuilds
 * default + deltas), so re-deriving would dump the PEOC onto the bare default
 * instead of their own view. The lens is a glance, never a mode: restore is exact
 * and nothing persists. `restore(enter(prefs).snapshot) === prefs`.
 */
export function exitMunicipalLens(snapshot: LayerPrefs): LayerPrefs {
  return snapshot;
}
